import { useEffect, useRef, useState } from 'react';
import { Crown, Medal, Goal, Lock, Check, Loader2, Search, X, ChevronDown } from 'lucide-react';
import {
  useWcTeams,
  useWcBonus,
  useWcSaveBonus,
  useWcScoring,
  useWcTeamPlayers,
  type WcTeam,
  type WcTeamRef,
  type WcPlayerSearchResult,
} from '@/lib/queries';
import { Flag, useViewerTimezone } from './common';
import { cn } from '@/lib/cn';

/** "sábado 4 de julio, 11:00 a.m." en la zona horaria del colaborador. */
function formatDeadline(iso: string | null | undefined, tz: string): string | null {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return new Intl.DateTimeFormat('es', {
    timeZone: tz,
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true,
  }).format(d);
}

/** Normaliza para buscar sin acentos ni mayúsculas. */
function norm(s: string): string {
  return s.normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase();
}

/** Cierra el panel al hacer clic fuera. */
function useClickOutside(ref: React.RefObject<HTMLElement | null>, onOutside: () => void) {
  useEffect(() => {
    const h = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onOutside();
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [ref, onOutside]);
}

/* ──────────────── Combo de selección (países, con buscador) ──────────────── */

function TeamCombo({
  teams,
  value,
  onChange,
  disabled,
  placeholder,
}: {
  teams: WcTeam[];
  value: WcTeamRef | null;
  onChange: (t: WcTeam | null) => void;
  disabled?: boolean;
  placeholder: string;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);
  useClickOutside(boxRef, () => setOpen(false));

  const filtered = q ? teams.filter((t) => norm(t.nameEs).includes(norm(q))) : teams;

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((v) => !v);
          setQ('');
        }}
        className={cn(
          'flex h-11 w-full items-center gap-2 rounded-md border border-border bg-white px-3 text-sm transition focus:outline-none',
          disabled ? 'cursor-not-allowed bg-bg text-font-tertiary' : 'hover:border-primary',
          open && 'border-primary ring-2 ring-primary/20',
        )}
      >
        {value ? (
          <>
            <Flag iso2={value.iso2} className="h-5 w-7 flex-shrink-0" />
            <span className="min-w-0 flex-1 truncate text-left font-semibold text-font-primary">
              {value.nameEs}
            </span>
            {!disabled && (
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                }}
                className="rounded-full p-1 text-font-tertiary hover:bg-bg hover:text-error"
              >
                <X size={13} />
              </span>
            )}
          </>
        ) : (
          <span className="flex-1 text-left text-font-tertiary">{placeholder}</span>
        )}
        <ChevronDown size={14} className={cn('flex-shrink-0 text-font-tertiary transition', open && 'rotate-180')} />
      </button>

      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-border bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b border-border-light px-3 py-2">
            <Search size={13} className="text-font-tertiary" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar selección..."
              className="flex-1 bg-transparent text-sm focus:outline-none"
            />
          </div>
          <div className="max-h-56 overflow-y-auto p-1">
            {filtered.length === 0 && (
              <p className="px-3 py-3 text-center text-xs text-font-tertiary">Sin resultados para "{q}"</p>
            )}
            {filtered.map((t) => (
              <button
                key={t.id}
                type="button"
                onClick={() => {
                  onChange(t);
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-bg"
              >
                <Flag iso2={t.iso2} className="h-4 w-6 flex-shrink-0" />
                <span className="min-w-0 flex-1 truncate">{t.nameEs}</span>
                <span className="rounded bg-bg px-1.5 py-0.5 text-[10px] font-semibold text-font-tertiary">
                  Grupo {t.groupLetter}
                </span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ─── Combo de jugador de UNA selección (Bota de Oro: país → jugador) ─── */

function PlayerOfTeamCombo({
  team,
  value,
  onChange,
  disabled,
}: {
  team: WcTeamRef;
  value: WcPlayerSearchResult | null;
  onChange: (p: WcPlayerSearchResult | null) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const boxRef = useRef<HTMLDivElement>(null);
  useClickOutside(boxRef, () => setOpen(false));
  const { data, isLoading } = useWcTeamPlayers(team.id);
  const all = data?.items ?? [];
  const players = q ? all.filter((p) => norm(p.jersey + ' ' + p.name).includes(norm(q))) : all;

  return (
    <div ref={boxRef} className="relative">
      <button
        type="button"
        disabled={disabled}
        onClick={() => {
          setOpen((v) => !v);
          setQ('');
        }}
        className={cn(
          'flex h-11 w-full items-center gap-2 rounded-md border border-border bg-white px-3 text-sm transition focus:outline-none',
          disabled ? 'cursor-not-allowed bg-bg text-font-tertiary' : 'hover:border-primary',
          open && 'border-primary ring-2 ring-primary/20',
        )}
      >
        {value ? (
          <>
            <Flag iso2={team.iso2} className="h-5 w-7 flex-shrink-0" />
            <span className="min-w-0 flex-1 truncate text-left">
              <span className="font-semibold text-font-primary">{value.jersey}</span>
              <span className="text-xs text-font-tertiary"> · #{value.number} · {value.position}</span>
            </span>
            {!disabled && (
              <span
                role="button"
                tabIndex={-1}
                onClick={(e) => {
                  e.stopPropagation();
                  onChange(null);
                }}
                className="rounded-full p-1 text-font-tertiary hover:bg-bg hover:text-error"
              >
                <X size={13} />
              </span>
            )}
          </>
        ) : (
          <span className="flex-1 text-left text-font-tertiary">
            Elige al goleador de {team.nameEs}
          </span>
        )}
        <ChevronDown size={14} className={cn('flex-shrink-0 text-font-tertiary transition', open && 'rotate-180')} />
      </button>

      {open && !disabled && (
        <div className="absolute z-30 mt-1 w-full overflow-hidden rounded-lg border border-border bg-white shadow-xl">
          <div className="flex items-center gap-2 border-b border-border-light px-3 py-2">
            <Search size={13} className="text-font-tertiary" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar jugador..."
              className="flex-1 bg-transparent text-sm focus:outline-none"
            />
            {isLoading && <Loader2 size={13} className="animate-spin text-font-tertiary" />}
          </div>
          <div className="max-h-56 overflow-y-auto p-1">
            {!isLoading && players.length === 0 && (
              <p className="px-3 py-3 text-center text-xs text-font-tertiary">Sin resultados para "{q}"</p>
            )}
            {players.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => {
                  onChange({
                    id: p.id,
                    number: p.number,
                    position: p.position,
                    jersey: p.jersey,
                    name: p.name,
                    team,
                  });
                  setOpen(false);
                }}
                className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-left text-sm hover:bg-bg"
              >
                <span className="w-6 text-right text-xs tabular-nums text-font-tertiary">{p.number}</span>
                <span className="min-w-0 flex-1 truncate font-medium">{p.jersey}</span>
                <span className="text-xs text-font-tertiary">{p.position}</span>
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/* ──────────────────────────── Bonus Picker ──────────────────────────── */

export function BonusPicker() {
  const { data: teamsData } = useWcTeams();
  const { data: bonus } = useWcBonus();
  const { data: cfg } = useWcScoring();
  const save = useWcSaveBonus();
  const teams = teamsData?.items ?? [];

  const [champion, setChampion] = useState<WcTeamRef | null>(null);
  const [runnerUp, setRunnerUp] = useState<WcTeamRef | null>(null);
  const [gbTeam, setGbTeam] = useState<WcTeamRef | null>(null);
  const [gbPlayer, setGbPlayer] = useState<WcPlayerSearchResult | null>(null);
  const [saved, setSaved] = useState(false);

  const tz = useViewerTimezone();
  const locked = bonus?.locked ?? false;
  const deadlineLabel = formatDeadline(bonus?.deadline, tz);

  // Repintar la selección guardada al volver a la página (entidades resueltas del API)
  useEffect(() => {
    if (!bonus) return;
    setChampion(bonus.championTeam ?? null);
    setRunnerUp(bonus.runnerUpTeam ?? null);
    setGbPlayer(bonus.goldenBootPlayer ?? null);
    setGbTeam(bonus.goldenBootPlayer?.team ?? null);
  }, [bonus]);

  const onSave = () => {
    save.mutate(
      {
        championTeamId: champion?.id ?? null,
        runnerUpTeamId: runnerUp?.id ?? null,
        goldenBootPlayerId: gbPlayer?.id ?? null,
      },
      {
        onSuccess: () => {
          setSaved(true);
          setTimeout(() => setSaved(false), 1800);
        },
      },
    );
  };

  return (
    <div className="rounded-xl border border-border bg-white p-5 shadow-sm">
      <div className="mb-1 flex items-center gap-2">
        <Crown size={18} className="text-warning" />
        <h3 className="text-base font-bold text-font-primary">Pronósticos de torneo</h3>
      </div>
      <p className="mb-4 text-xs text-font-secondary">
        Elige tu campeón, subcampeón y la Bota de Oro. {locked
          ? 'Ya iniciaron los octavos de final: estos pronósticos están cerrados.'
          : deadlineLabel
            ? `Puedes cambiarlos hasta el inicio de los octavos de final: ${deadlineLabel}.`
            : 'Puedes cambiarlos hasta el inicio de los octavos de final.'}
      </p>

      {locked ? (
        <div className="mb-4 flex items-center gap-2 rounded-lg bg-bg p-2.5 text-xs font-semibold text-font-secondary">
          <Lock size={13} /> Pronósticos de torneo bloqueados (iniciaron los octavos)
        </div>
      ) : (
        deadlineLabel && (
          <div className="mb-4 flex items-center gap-2 rounded-lg bg-warning/10 p-2.5 text-xs font-semibold text-warning">
            <Lock size={13} /> Cierra el {deadlineLabel}
          </div>
        )
      )}

      <div className="space-y-4">
        <div>
          <label className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-font-primary">
            <Crown size={14} className="text-warning" /> Campeón
            <span className="ml-auto text-xs font-normal text-font-tertiary">
              {cfg ? `${cfg.pointsChampion} pts` : ''}
            </span>
          </label>
          <TeamCombo
            teams={teams}
            value={champion}
            onChange={(t) => setChampion(t)}
            disabled={locked}
            placeholder="Busca y elige al campeón"
          />
        </div>

        <div>
          <label className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-font-primary">
            <Medal size={14} className="text-info" /> Subcampeón
            <span className="ml-auto text-xs font-normal text-font-tertiary">
              {cfg ? `${cfg.pointsFinalist} pts` : ''}
            </span>
          </label>
          <TeamCombo
            teams={teams}
            value={runnerUp}
            onChange={(t) => setRunnerUp(t)}
            disabled={locked}
            placeholder="Busca y elige al subcampeón"
          />
        </div>

        <div>
          <label className="mb-1.5 flex items-center gap-1.5 text-sm font-semibold text-font-primary">
            <Goal size={14} className="text-accent" /> Bota de Oro (máximo goleador)
            <span className="ml-auto text-xs font-normal text-font-tertiary">
              {cfg ? `${cfg.pointsGoldenBoot} pts` : ''}
            </span>
          </label>
          <div className="space-y-2">
            <TeamCombo
              teams={teams}
              value={gbTeam}
              onChange={(t) => {
                setGbTeam(t);
                setGbPlayer(null);
              }}
              disabled={locked}
              placeholder="Busca la selección del goleador"
            />
            {gbTeam && (
              <PlayerOfTeamCombo
                team={gbTeam}
                value={gbPlayer}
                onChange={setGbPlayer}
                disabled={locked}
              />
            )}
          </div>
        </div>
      </div>

      {!locked && (
        <button
          onClick={onSave}
          disabled={save.isPending}
          className="mt-5 flex h-10 w-full items-center justify-center gap-2 rounded-md bg-primary text-sm font-semibold text-white shadow-sm transition hover:bg-primary-dark disabled:opacity-60"
        >
          {save.isPending ? (
            <Loader2 size={15} className="animate-spin" />
          ) : saved ? (
            <Check size={15} />
          ) : (
            <Crown size={15} />
          )}
          {saved ? 'Pronósticos guardados' : 'Guardar pronósticos'}
        </button>
      )}
    </div>
  );
}
